import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../constants/api';
import { useNotification } from '../../contexts/NotificationContext';
import { usePlan } from '../../contexts/PlanContext';
import { useLanguage } from '../../contexts/LanguageContext';

const FONT = "'abcNormal','Inter','Arial',sans-serif";
const FONT_UI = "'Inter',system-ui,sans-serif";
const GRAY = '#9A9A9A';
const BLUE = '#0A0A0A';

const STATUS_LABELS = { scheduled: 'Programada', active: 'Activa', ended: 'Finalizada' };
const FUNNEL_ORDER = ['new', 'contacted', 'qualified', 'meeting_scheduled', 'proposal_sent', 'won', 'lost'];
const FUNNEL_LABELS = {
  new: 'Nuevo',
  contacted: 'Contactado',
  qualified: 'Calificado',
  meeting_scheduled: 'Reunión agendada',
  proposal_sent: 'Propuesta enviada',
  won: 'Ganado',
  lost: 'Perdido',
};

const CSS = `
  .cp-wrap{font-family:${FONT_UI};}
  .cp-title{font-family:${FONT};font-weight:300;letter-spacing:-0.01em;color:#0A0A0A;}
  .cp-subtitle{font-family:${FONT_UI};color:#6B6B6B;}
  .cp-header{display:flex;justify-content:space-between;align-items:flex-end;flex-wrap:wrap;gap:12px;margin-bottom:24px;}
  .cp-btn{font-size:13.5px;font-weight:600;padding:10px 18px;border-radius:9px;cursor:pointer;text-align:center;letter-spacing:-0.01em;transition:background .15s,border-color .15s,opacity .15s;display:inline-flex;align-items:center;justify-content:center;gap:7px;border:none;font-family:${FONT_UI};}
  .cp-btn-primary{background:#0A0A0A;color:#fff;}
  .cp-btn-primary:hover{background:#1a1a1a;}
  .cp-btn-ghost{background:#fff;color:#0A0A0A;border:1px solid #E5E5E5;}
  .cp-btn-ghost:hover{background:#FAFAFA;}
  .cp-btn-danger{background:#fff;color:#DC2626;border:1px solid rgba(220,38,38,.25);}
  .cp-btn-danger:hover{background:rgba(220,38,38,.06);}
  .cp-btn:disabled{opacity:.5;cursor:not-allowed;}

  .cp-table-card{background:#fff;border:1px solid #E5E5E5;border-radius:14px;overflow:hidden;}
  .cp-row{display:flex;align-items:center;gap:14px;padding:16px 20px;border-bottom:1px solid #F0F0F0;cursor:pointer;transition:background .12s;}
  .cp-row:last-child{border-bottom:none;}
  .cp-row:hover{background:#FAFAFA;}
  .cp-row-name{font-size:13.5px;font-weight:600;color:#0A0A0A;}
  .cp-row-dates{font-size:12px;color:${GRAY};margin-top:2px;}
  .cp-row-count{font-size:12.5px;color:#6B6B6B;white-space:nowrap;}

  .cp-badge{display:inline-flex;align-items:center;gap:5px;padding:3px 10px;border-radius:980px;font-size:11.5px;font-weight:600;white-space:nowrap;}
  .cp-badge--scheduled{background:rgba(217,119,6,.1);color:#D97706;}
  .cp-badge--active{background:rgba(22,163,74,.1);color:#16A34A;}
  .cp-badge--ended{background:rgba(0,0,0,.06);color:#6B6B6B;}
  .cp-badge-dot{width:5px;height:5px;border-radius:50%;background:currentColor;flex-shrink:0;}

  .cp-empty{padding:56px 24px;text-align:center;}
  .cp-empty-icon{width:52px;height:52px;border-radius:50%;background:#F5F5F5;display:flex;align-items:center;justify-content:center;margin:0 auto 16px;color:#A3A3A3;}
  .cp-empty-title{font-size:16px;font-weight:700;color:#0A0A0A;margin-bottom:6px;}
  .cp-empty-sub{font-size:13px;color:${GRAY};max-width:380px;margin:0 auto;line-height:1.5;}

  .cp-back{display:inline-flex;align-items:center;gap:6px;font-size:13px;color:#6B6B6B;background:none;border:none;cursor:pointer;font-family:${FONT_UI};transition:color .15s;padding:0;margin-bottom:18px;}
  .cp-back:hover{color:#0A0A0A;}

  .cp-detail-header{display:flex;justify-content:space-between;align-items:flex-start;flex-wrap:wrap;gap:16px;margin-bottom:24px;}
  .cp-detail-name{font-size:22px;font-weight:700;color:#0A0A0A;letter-spacing:-0.01em;margin-bottom:6px;}
  .cp-detail-dates{font-size:13px;color:${GRAY};}
  .cp-detail-actions{display:flex;gap:8px;flex-wrap:wrap;}

  .cp-metrics{display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:12px;margin-bottom:24px;}
  .cp-metric-card{background:#fff;border:1px solid #E5E5E5;border-radius:14px;padding:16px 18px;}
  .cp-metric-value{font-size:24px;font-weight:700;color:#0A0A0A;letter-spacing:-0.02em;}
  .cp-metric-label{font-size:11.5px;color:${GRAY};margin-top:4px;}

  .cp-section-title{font-size:15px;font-weight:700;color:#0A0A0A;margin-bottom:14px;}
  .cp-card{background:#fff;border:1px solid #E5E5E5;border-radius:16px;padding:22px 24px;margin-bottom:20px;}

  .cp-funnel-row{display:flex;align-items:center;gap:12px;padding:8px 10px;margin:0 -10px;border-radius:10px;cursor:pointer;transition:background .12s;}
  .cp-funnel-row:hover{background:#FAFAFA;}
  .cp-funnel-label{width:150px;flex-shrink:0;font-size:12.5px;color:#0A0A0A;font-weight:500;}
  .cp-funnel-bar{flex:1;height:8px;border-radius:99px;background:#F0F0F0;overflow:hidden;}
  .cp-funnel-fill{height:100%;background:${BLUE};border-radius:99px;transition:width .3s ease;}
  .cp-funnel-count{min-width:64px;text-align:right;font-size:12.5px;font-weight:600;color:#0A0A0A;white-space:nowrap;}
  .cp-row-chevron{color:${GRAY};flex-shrink:0;transition:transform .15s;}
  .cp-row-chevron--open{transform:rotate(180deg);}

  .cp-avatar{width:30px;height:30px;border-radius:50%;background:#F0F0F0;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;color:#6B6B6B;overflow:hidden;flex-shrink:0;}
  .cp-leader-card{display:flex;align-items:center;gap:10px;padding:10px;margin:0 -10px;border-top:1px solid rgba(0,0,0,0.05);border-radius:10px;cursor:pointer;transition:background .12s;}
  .cp-leader-card:first-child{border-top:none;}
  .cp-leader-card:hover{background:#FAFAFA;}
  .cp-leader-stats{display:flex;gap:14px;font-size:11px;color:#6B6B6B;margin-top:2px;}
  .cp-leader-stats b{color:#0A0A0A;font-weight:600;}

  .cp-metric-card--clickable{cursor:pointer;transition:border-color .15s,box-shadow .15s;}
  .cp-metric-card--clickable:hover{border-color:#D4D4D4;box-shadow:0 2px 8px rgba(0,0,0,.04);}

  .cp-contact-list{padding:2px 10px 8px;margin:0 -10px;}
  .cp-contact-row{display:flex;align-items:center;gap:10px;padding:8px 0;border-top:1px dashed #F0F0F0;}
  .cp-contact-row:first-child{border-top:none;}
  .cp-contact-name{font-size:12.5px;font-weight:600;color:#0A0A0A;}
  .cp-contact-meta{font-size:11.5px;color:${GRAY};margin-top:1px;}
  .cp-contact-empty{padding:10px 0;text-align:center;font-size:12px;color:${GRAY};}

  .cp-form-label{font-size:12px;font-weight:600;color:#0A0A0A;margin-bottom:5px;display:block;}
  .cp-form-input{width:100%;padding:10px 12px;border:1px solid #E5E5E5;border-radius:9px;font-size:13.5px;font-family:${FONT_UI};color:#0A0A0A;outline:none;transition:border-color .15s;}
  .cp-form-input:focus{border-color:#0A0A0A;}
  .cp-form-row{display:grid;grid-template-columns:1fr 1fr;gap:12px;}
  .cp-profile-pick{display:flex;align-items:center;gap:9px;padding:9px 10px;border-radius:9px;border:1px solid #E5E5E5;cursor:pointer;margin-bottom:6px;transition:background .12s,border-color .12s;}
  .cp-profile-pick:hover{background:#FAFAFA;}
  .cp-profile-pick--on{border-color:#0A0A0A;background:#FAFAFA;}
  .cp-modal-actions{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-top:20px;}
  .cp-modal-btn{padding:12px 8px;border-radius:12px;font-weight:600;font-size:12.5px;border:1px solid #E5E5E5;background:#fff;color:#0A0A0A;cursor:pointer;transition:background .15s,border-color .15s;font-family:${FONT_UI};}
  .cp-modal-btn:hover{background:#FAFAFA;border-color:#D4D4D4;}
  .cp-modal-btn:disabled{opacity:.5;cursor:not-allowed;}
  .cp-modal-btn--primary{background:#0A0A0A;border-color:#0A0A0A;color:#fff;}
  .cp-modal-btn--primary:hover{background:#1a1a1a;border-color:#1a1a1a;}

  .cp-hiw-steps{display:grid;grid-template-columns:repeat(auto-fit,minmax(210px,1fr));gap:18px;margin-bottom:22px;}
  .cp-hiw-step{display:flex;gap:12px;align-items:flex-start;}
  .cp-hiw-num{width:24px;height:24px;border-radius:50%;background:#0A0A0A;color:#fff;font-size:11.5px;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
  .cp-hiw-step-title{font-size:12.5px;font-weight:600;color:#0A0A0A;margin-bottom:3px;}
  .cp-hiw-step-text{font-size:12px;color:#6B6B6B;line-height:1.45;}
  .cp-hiw-example-label{font-size:11px;font-weight:600;color:${GRAY};text-transform:uppercase;letter-spacing:.04em;margin-bottom:10px;}
  .cp-hiw-example-card{background:#FAFAFA;border:1px solid #F0F0F0;border-radius:12px;padding:16px 18px;}
`;

// campaign.status ('scheduled'|'active'|'ended') and funnel keys (shared
// with HubPanel's contact.status enum) are never translated, only labeled.
const statusLabel = (status, t) => t(`campaigns.status.${status}`, STATUS_LABELS[status] || status);
const funnelLabel = (key, t) => t(`hub.status.${key}`, FUNNEL_LABELS[key] || key);

const fmtDate = (iso, locale = 'es-CL') => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return ''; }
};

const fmtCLP = (n, locale = 'es-CL') => {
  if (n == null || Number.isNaN(n)) return '';
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(n);
  } catch { return `$${Math.round(n)}`; }
};

const CampaignsPanel = ({ profiles = [] }) => {
  const { confirm, toast } = useNotification();
  const { hasFeature } = usePlan();
  const { t, lang } = useLanguage();
  const dateLocale = lang === 'en' ? 'en-US' : 'es-CL';

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', starts_at: '', ends_at: '', linked_profile_ids: [], budget: '' });
  const [showHowItWorks, setShowHowItWorks] = useState(false);

  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [budgetInput, setBudgetInput] = useState('');
  const [savingBudget, setSavingBudget] = useState(false);

  // Which funnel stage or leaderboard member is currently expanded inline,
  // showing its real contact list — { type: 'status'|'profile', key } | null.
  const [expanded, setExpanded] = useState(null);
  const [expandedContacts, setExpandedContacts] = useState([]);
  const [expandedLoading, setExpandedLoading] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/campaigns`, { headers });
      setCampaigns(res.data.campaigns || []);
    } catch (e) {
      console.error('Error loading campaigns:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  const loadDetail = useCallback(async (id) => {
    setLoadingDetail(true);
    try {
      const res = await axios.get(`${API}/campaigns/${id}`, { headers });
      setDetail(res.data);
    } catch (e) {
      console.error('Error loading campaign detail:', e);
      toast.error(t('campaigns.toast.loadError', 'No se pudo cargar el evento'));
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
    else setDetail(null);
    setExpanded(null);
    setExpandedContacts([]);
  }, [selectedId, loadDetail]);

  // Powers the click-to-expand on funnel rows, KPI cards, and leaderboard
  // rows: reuses GET /contacts?campaign_id=...&status=...|&origin_profile_id=...,
  // which already existed for Hub/Leads filtering — no backend changes needed.
  const toggleExpand = async (type, key) => {
    if (expanded && expanded.type === type && expanded.key === key) {
      setExpanded(null);
      return;
    }
    setExpanded({ type, key });
    setExpandedContacts([]);
    setExpandedLoading(true);
    try {
      const params = { campaign_id: detail.campaign.id, limit: 200 };
      if (type === 'status') params.status = key;
      else params.origin_profile_id = key;
      const res = await axios.get(`${API}/contacts`, { headers, params });
      setExpandedContacts(res.data.contacts || []);
    } catch (e) {
      toast.error(t('campaigns.toast.contactsLoadError', 'No se pudieron cargar los contactos'));
      setExpanded(null);
    } finally {
      setExpandedLoading(false);
    }
  };

  const openCreate = () => {
    setForm({ name: '', starts_at: '', ends_at: '', linked_profile_ids: [], budget: '' });
    setShowCreate(true);
  };

  const toggleProfile = (id) => {
    setForm((f) => ({
      ...f,
      linked_profile_ids: f.linked_profile_ids.includes(id)
        ? f.linked_profile_ids.filter((p) => p !== id)
        : [...f.linked_profile_ids, id],
    }));
  };

  const handleCreate = async () => {
    if (!form.name.trim() || !form.starts_at) return;
    if (form.ends_at && form.ends_at < form.starts_at) {
      toast.error(t('campaigns.toast.endBeforeStart', 'La fecha de término no puede ser anterior a la fecha de inicio.'));
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/campaigns`, {
        name: form.name.trim(),
        starts_at: new Date(form.starts_at).toISOString(),
        ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
        linked_profile_ids: form.linked_profile_ids,
        budget: form.budget ? parseFloat(form.budget) : null,
      }, { headers });
      toast.success(t('campaigns.toast.created', 'Evento creado'));
      setShowCreate(false);
      await loadCampaigns();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('campaigns.toast.createError', 'No se pudo crear el evento'));
    } finally {
      setCreating(false);
    }
  };

  const handleSetStatus = async (campaign, status) => {
    try {
      await axios.put(`${API}/campaigns/${campaign.id}`, { status }, { headers });
      toast.success(status === 'active' ? t('campaigns.toast.activated', 'Evento activado') : t('campaigns.toast.ended', 'Evento finalizado'));
      await loadCampaigns();
      if (selectedId === campaign.id) loadDetail(campaign.id);
    } catch (e) {
      toast.error(t('campaigns.toast.updateError', 'No se pudo actualizar el evento'));
    }
  };

  const openBudgetModal = () => {
    setBudgetInput(detail?.campaign?.budget != null ? String(detail.campaign.budget) : '');
    setShowBudgetModal(true);
  };

  const handleSaveBudget = async () => {
    setSavingBudget(true);
    try {
      await axios.put(`${API}/campaigns/${detail.campaign.id}`, {
        budget: budgetInput ? parseFloat(budgetInput) : null,
      }, { headers });
      toast.success(t('campaigns.toast.budgetUpdated', 'Presupuesto actualizado'));
      setShowBudgetModal(false);
      await loadDetail(detail.campaign.id);
      await loadCampaigns();
    } catch (e) {
      toast.error(t('campaigns.toast.budgetError', 'No se pudo actualizar el presupuesto'));
    } finally {
      setSavingBudget(false);
    }
  };

  const handleDelete = async (campaign) => {
    const ok = await confirm(
      t('campaigns.confirm.delete', `¿Eliminar "${campaign.name}"? Esta acción no se puede deshacer.`).replace('{name}', campaign.name),
      { title: t('campaigns.confirm.deleteTitle', 'Eliminar evento'), confirmLabel: t('campaigns.confirm.deleteLabel', 'Eliminar') }
    );
    if (!ok) return;
    try {
      await axios.delete(`${API}/campaigns/${campaign.id}`, { headers });
      toast.success(t('campaigns.toast.deleted', 'Evento eliminado'));
      if (selectedId === campaign.id) setSelectedId(null);
      await loadCampaigns();
    } catch (e) {
      toast.error(t('campaigns.toast.deleteError', 'No se pudo eliminar el evento'));
    }
  };

  const handleExport = async (campaign) => {
    try {
      const res = await axios.get(`${API}/campaigns/${campaign.id}/export`, { headers, responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${t('campaigns.exportFilename', 'Evento')}_${campaign.name.replace(/\s+/g, '_')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(t('campaigns.toast.exportError', 'No se pudo exportar el evento'));
    }
  };

  const showTeamsLeaderboard = hasFeature('teams');
  // No hay nada más que mostrar cuando la cuenta todavía no tiene ningún
  // evento — en ese caso el "Cómo funciona" deja de ser opcional y se
  // muestra siempre, abierto, en vez de esconderse detrás del botón (que
  // es justo el momento en que más falta hace explicarlo).
  const isEmpty = !loading && campaigns.length === 0;

  const renderExpandedContacts = () => (
    <div className="cp-contact-list">
      {expandedLoading ? (
        <div className="cp-contact-empty">{t('campaigns.loading', 'Cargando…')}</div>
      ) : expandedContacts.length === 0 ? (
        <div className="cp-contact-empty">{t('campaigns.noContacts', 'Sin contactos.')}</div>
      ) : (
        <>
          {expandedContacts.map((c) => (
            <div key={c.id} className="cp-contact-row">
              <div className="cp-avatar">{(c.full_name || '?')[0].toUpperCase()}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p className="cp-contact-name">{c.full_name}</p>
                <p className="cp-contact-meta">
                  {[c.position, c.company].filter(Boolean).join(' · ') || c.email || c.phone || t('campaigns.noAdditionalData', 'Sin datos adicionales')}
                </p>
              </div>
            </div>
          ))}
          {expandedContacts.length === 200 && (
            <p className="cp-contact-empty" style={{ fontStyle: 'italic' }}>{t('campaigns.first200', 'Mostrando los primeros 200 contactos.')}</p>
          )}
        </>
      )}
    </div>
  );

  const ChevronIcon = ({ open }) => (
    <svg className={`cp-row-chevron${open ? ' cp-row-chevron--open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
    </svg>
  );

  return (
    <div className="w-full dashboard-container p-6 cp-wrap">
      <style>{CSS}</style>

      {!selectedId ? (
        <>
          <div className="cp-header">
            <div>
              <h1 className="cp-title" style={{ fontSize: 26 }}>{t('campaigns.title', 'Eventos')}</h1>
              <p className="cp-subtitle" style={{ fontSize: 14, marginTop: 4 }}>
                {t('campaigns.subtitle', 'Agrupa tus leads por evento o feria y mide qué tan bien te fue.')}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {!isEmpty && (
                <button className="cp-btn cp-btn-ghost" onClick={() => setShowHowItWorks((v) => !v)}>
                  {showHowItWorks ? t('campaigns.hide', 'Ocultar') : t('campaigns.howItWorks', '¿Cómo funciona?')}
                </button>
              )}
              <button className="cp-btn cp-btn-primary" onClick={openCreate}>+ {t('campaigns.createEvent', 'Crear evento')}</button>
            </div>
          </div>

          {(showHowItWorks || isEmpty) && (
            <div className="cp-card cp-howitworks">
              <div className="cp-section-title">{t('campaigns.howItWorks2', 'Cómo funciona')}</div>
              <div className="cp-hiw-steps">
                <div className="cp-hiw-step">
                  <div className="cp-hiw-num">1</div>
                  <div>
                    <p className="cp-hiw-step-title">{t('campaigns.hiw.step1Title', 'Antes del evento')}</p>
                    <p className="cp-hiw-step-text">{t('campaigns.hiw.step1Text', 'Crea un evento con el nombre, las fechas y el perfil que vas a usar en el stand.')}</p>
                  </div>
                </div>
                <div className="cp-hiw-step">
                  <div className="cp-hiw-num">2</div>
                  <div>
                    <p className="cp-hiw-step-title">{t('campaigns.hiw.step2Title', 'Durante el evento')}</p>
                    <p className="cp-hiw-step-text">{t('campaigns.hiw.step2Text', 'Cada vez que alguien escanea tu tarjeta y llena el formulario, el lead queda vinculado al evento automáticamente — el visitante no nota nada distinto.')}</p>
                  </div>
                </div>
                <div className="cp-hiw-step">
                  <div className="cp-hiw-num">3</div>
                  <div>
                    <p className="cp-hiw-step-title">{t('campaigns.hiw.step3Title', '¿Te dieron una tarjeta física?')}</p>
                    <p className="cp-hiw-step-text">{t('campaigns.hiw.step3Text', 'Agrégalo manualmente desde Hub — si hay un evento activo, se pre-selecciona sola.')}</p>
                  </div>
                </div>
                <div className="cp-hiw-step">
                  <div className="cp-hiw-num">4</div>
                  <div>
                    <p className="cp-hiw-step-title">{t('campaigns.hiw.step4Title', 'Después del evento')}</p>
                    <p className="cp-hiw-step-text">{t('campaigns.hiw.step4Text', 'Finaliza el evento y revisa el embudo completo: cuántos leads, cuántos calificaron, cuántos se ganaron.')}</p>
                  </div>
                </div>
              </div>

              <p className="cp-hiw-example-label">{t('campaigns.hiw.realExample', 'Ejemplo real')}</p>
              <div className="cp-hiw-example-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <div>
                    <p style={{ fontSize: 13.5, fontWeight: 600, color: '#0A0A0A' }}>Expo Retail 2026</p>
                    <p style={{ fontSize: 11.5, color: GRAY, marginTop: 2 }}>19 ago – 21 ago 2026</p>
                  </div>
                  <span className="cp-badge cp-badge--ended"><span className="cp-badge-dot" />{statusLabel('ended', t)}</span>
                </div>
                {[['new', 42], ['contacted', 14], ['qualified', 7], ['won', 3]].map(([key, count]) => (
                  <div className="cp-funnel-row" key={key} style={{ padding: '5px 0' }}>
                    <span className="cp-funnel-label" style={{ width: 110, fontSize: 11.5 }}>{funnelLabel(key, t)}</span>
                    <div className="cp-funnel-bar"><div className="cp-funnel-fill" style={{ width: `${(count / 42) * 100}%` }} /></div>
                    <span className="cp-funnel-count" style={{ fontSize: 11.5 }}>{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="cp-table-card">
            {loading ? (
              <div style={{ padding: 40, textAlign: 'center', color: GRAY, fontSize: 13 }}>{t('campaigns.loading', 'Cargando…')}</div>
            ) : campaigns.length === 0 ? (
              <div className="cp-empty">
                <div className="cp-empty-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 22V15" />
                  </svg>
                </div>
                <p className="cp-empty-title">{t('campaigns.empty.title', 'Aún no tienes eventos')}</p>
                <p className="cp-empty-sub">{t('campaigns.empty.desc', 'Crea uno para tu próxima feria o evento y mide automáticamente cuántos leads capturaste ahí.')}</p>
                <button className="cp-btn cp-btn-primary" style={{ marginTop: 18 }} onClick={openCreate}>
                  + {t('campaigns.empty.createFirst', 'Crear tu primer evento')}
                </button>
              </div>
            ) : (
              campaigns.map((c) => (
                <div key={c.id} className="cp-row" onClick={() => setSelectedId(c.id)}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="cp-row-name">{c.name}</div>
                    <div className="cp-row-dates">
                      {fmtDate(c.starts_at, dateLocale)}{c.ends_at ? ` – ${fmtDate(c.ends_at, dateLocale)}` : ''}
                    </div>
                  </div>
                  <span className="cp-row-count">{c.contact_count} {c.contact_count === 1 ? t('campaigns.leadSingular', 'lead') : t('campaigns.leadPlural', 'leads')}</span>
                  <span className={`cp-badge cp-badge--${c.status}`}>
                    <span className="cp-badge-dot" />
                    {statusLabel(c.status, t)}
                  </span>
                </div>
              ))
            )}
          </div>
        </>
      ) : (
        <>
          <button className="cp-back" onClick={() => setSelectedId(null)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            {t('campaigns.title', 'Eventos')}
          </button>

          {loadingDetail || !detail ? (
            <div style={{ padding: 40, textAlign: 'center', color: GRAY, fontSize: 13 }}>{t('campaigns.loading', 'Cargando…')}</div>
          ) : (
            <>
              <div className="cp-detail-header">
                <div>
                  <div className="cp-detail-name">{detail.campaign.name}</div>
                  <div className="cp-detail-dates">
                    {fmtDate(detail.campaign.starts_at, dateLocale)}{detail.campaign.ends_at ? ` – ${fmtDate(detail.campaign.ends_at, dateLocale)}` : ''}
                    {' · '}
                    <span className={`cp-badge cp-badge--${detail.campaign.status}`} style={{ marginLeft: 4 }}>
                      <span className="cp-badge-dot" />
                      {statusLabel(detail.campaign.status, t)}
                    </span>
                  </div>
                </div>
                <div className="cp-detail-actions">
                  {detail.campaign.status === 'scheduled' && (
                    <button className="cp-btn cp-btn-ghost" onClick={() => handleSetStatus(detail.campaign, 'active')}>{t('campaigns.activate', 'Activar')}</button>
                  )}
                  {detail.campaign.status === 'active' && (
                    <button className="cp-btn cp-btn-ghost" onClick={() => handleSetStatus(detail.campaign, 'ended')}>{t('campaigns.end', 'Finalizar')}</button>
                  )}
                  <button className="cp-btn cp-btn-ghost" onClick={openBudgetModal}>
                    {detail.campaign.budget != null ? t('campaigns.editBudget', 'Editar presupuesto') : `+ ${t('campaigns.budget', 'Presupuesto')}`}
                  </button>
                  <button className="cp-btn cp-btn-ghost" onClick={() => handleExport(detail.campaign)}>{t('analytics.export', 'Exportar')}</button>
                  <button className="cp-btn cp-btn-danger" onClick={() => handleDelete(detail.campaign)}>{t('campaigns.delete', 'Eliminar')}</button>
                </div>
              </div>

              <div className="cp-metrics">
                <div className="cp-metric-card">
                  <div className="cp-metric-value">{detail.total_contacts}</div>
                  <div className="cp-metric-label">{t('campaigns.totalLeads', 'Leads totales')}</div>
                </div>
                {FUNNEL_ORDER.filter((k) => ['contacted', 'qualified', 'won'].includes(k)).map((k) => {
                  const row = detail.funnel.find((f) => f.status === k);
                  return (
                    <div className="cp-metric-card cp-metric-card--clickable" key={k} onClick={() => toggleExpand('status', k)}>
                      <div className="cp-metric-value">{row ? row.count : 0}</div>
                      <div className="cp-metric-label">{funnelLabel(k, t)}</div>
                    </div>
                  );
                })}
                {detail.campaign.budget != null && (
                  <>
                    <div className="cp-metric-card">
                      <div className="cp-metric-value" style={{ fontSize: 18 }}>
                        {detail.cost_per_lead != null ? fmtCLP(detail.cost_per_lead, dateLocale) : '—'}
                      </div>
                      <div className="cp-metric-label">{t('campaigns.costPerLead', 'Costo por lead')}</div>
                    </div>
                    <div className="cp-metric-card">
                      <div className="cp-metric-value" style={{ fontSize: 18 }}>
                        {detail.cost_per_qualified_lead != null ? fmtCLP(detail.cost_per_qualified_lead, dateLocale) : '—'}
                      </div>
                      <div className="cp-metric-label">{t('campaigns.costPerQualifiedLead', 'Costo por lead calificado')}</div>
                    </div>
                  </>
                )}
              </div>

              {detail.campaign.budget == null && (
                <p style={{ fontSize: 12, color: GRAY, marginTop: -14, marginBottom: 20 }}>
                  {t('campaigns.addBudgetHint', 'Agrega el presupuesto del evento para ver el costo por lead.')} <button onClick={openBudgetModal} style={{ color: '#0A0A0A', fontWeight: 600, textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: FONT_UI, fontSize: 12 }}>{t('campaigns.add', 'Agregar')}</button>
                </p>
              )}

              <div className="cp-card">
                <div className="cp-section-title">{t('campaigns.funnel', 'Embudo')}</div>
                {detail.total_contacts === 0 ? (
                  <p style={{ fontSize: 12.5, color: GRAY }}>{t('campaigns.noLeadsYet', 'Todavía no hay leads en este evento.')}</p>
                ) : (
                  FUNNEL_ORDER.map((k) => {
                    const row = detail.funnel.find((f) => f.status === k);
                    const count = row ? row.count : 0;
                    const pct = detail.total_contacts ? (count / detail.total_contacts) * 100 : 0;
                    if (!count) return null;
                    const isOpen = expanded?.type === 'status' && expanded.key === k;
                    return (
                      <div key={k}>
                        <div className="cp-funnel-row" onClick={() => toggleExpand('status', k)}>
                          <span className="cp-funnel-label">{funnelLabel(k, t)}</span>
                          <div className="cp-funnel-bar"><div className="cp-funnel-fill" style={{ width: `${pct}%` }} /></div>
                          <span className="cp-funnel-count">{count} · {pct.toFixed(0)}%</span>
                          <ChevronIcon open={isOpen} />
                        </div>
                        {isOpen && renderExpandedContacts()}
                      </div>
                    );
                  })
                )}
              </div>

              {showTeamsLeaderboard && detail.leaderboard.length > 0 && (
                <div className="cp-card">
                  <div className="cp-section-title">{t('campaigns.byTeamMember', 'Por miembro del equipo')}</div>
                  {detail.leaderboard.map((m) => {
                    const isOpen = expanded?.type === 'profile' && expanded.key === m.profile_id;
                    return (
                      <div key={m.profile_id}>
                        <div className="cp-leader-card" onClick={() => toggleExpand('profile', m.profile_id)}>
                          <div className="cp-avatar">
                            {m.profile_image_url
                              ? <img src={m.profile_image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              : (m.name || '?')[0].toUpperCase()}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ fontSize: 12.5, fontWeight: 500, color: '#0A0A0A' }}>{m.name}</p>
                            <div className="cp-leader-stats">
                              <span><b>{m.contacts}</b> {t('campaigns.leadPlural', 'leads')}</span>
                              <span><b>{m.qualified_or_won}</b> {t('campaigns.qualified', 'calificados')}</span>
                            </div>
                          </div>
                          <ChevronIcon open={isOpen} />
                        </div>
                        {isOpen && renderExpandedContacts()}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Create Campaign Modal */}
      {showCreate && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => !creating && setShowCreate(false)}
        >
          <div className="relative max-w-sm w-full animate-fadeIn" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => !creating && setShowCreate(false)}
              disabled={creating}
              className="absolute -top-3 -right-3 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center text-gray-600 hover:text-gray-800 hover:scale-110 transition-all z-10"
              style={{ border: '1px solid #E5E5E5' }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="bg-white rounded-3xl p-8 shadow-2xl overflow-hidden" style={{ border: '1px solid #E5E5E5' }}>
              <div className="cp-section-title" style={{ marginBottom: 18 }}>{t('campaigns.newEvent', 'Nuevo evento')}</div>

              <label className="cp-form-label">{t('campaigns.form.name', 'Nombre')}</label>
              <input
                type="text"
                className="cp-form-input"
                placeholder="Expo Retail 2026"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                style={{ marginBottom: 14 }}
                autoFocus
              />

              <div className="cp-form-row" style={{ marginBottom: 14 }}>
                <div>
                  <label className="cp-form-label">{t('campaigns.form.startDate', 'Fecha inicio')}</label>
                  <input
                    type="date"
                    className="cp-form-input"
                    value={form.starts_at}
                    onChange={(e) => setForm((f) => ({ ...f, starts_at: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="cp-form-label">{t('campaigns.form.endDate', 'Fecha término')}</label>
                  <input
                    type="date"
                    className="cp-form-input"
                    value={form.ends_at}
                    onChange={(e) => setForm((f) => ({ ...f, ends_at: e.target.value }))}
                  />
                </div>
              </div>

              <label className="cp-form-label">{t('campaigns.form.budgetOptional', 'Presupuesto del evento (CLP, opcional)')}</label>
              <input
                type="number"
                min="0"
                className="cp-form-input"
                placeholder={t('campaigns.form.budgetPlaceholder', 'Ej: 500000')}
                value={form.budget}
                onChange={(e) => setForm((f) => ({ ...f, budget: e.target.value }))}
                style={{ marginBottom: 14 }}
              />

              {profiles.length > 0 && (
                <>
                  <label className="cp-form-label">{t('campaigns.form.profilesToUse', 'Perfiles que se usarán')}</label>
                  <div style={{ maxHeight: 160, overflowY: 'auto', marginBottom: 6 }}>
                    {profiles.map((p) => (
                      <div
                        key={p.id}
                        className={`cp-profile-pick${form.linked_profile_ids.includes(p.id) ? ' cp-profile-pick--on' : ''}`}
                        onClick={() => toggleProfile(p.id)}
                      >
                        <input type="checkbox" readOnly checked={form.linked_profile_ids.includes(p.id)} />
                        <span style={{ fontSize: 13 }}>{p.name}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <div className="cp-modal-actions">
                <button className="cp-modal-btn" onClick={() => setShowCreate(false)} disabled={creating}>{t('hub.cancel', 'Cancelar')}</button>
                <button
                  className="cp-modal-btn cp-modal-btn--primary"
                  onClick={handleCreate}
                  disabled={creating || !form.name.trim() || !form.starts_at}
                >
                  {creating ? t('campaigns.creating', 'Creando…') : t('campaigns.createEvent', 'Crear evento')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Budget Modal */}
      {showBudgetModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => !savingBudget && setShowBudgetModal(false)}
        >
          <div className="relative max-w-sm w-full animate-fadeIn" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => !savingBudget && setShowBudgetModal(false)}
              disabled={savingBudget}
              className="absolute -top-3 -right-3 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center text-gray-600 hover:text-gray-800 hover:scale-110 transition-all z-10"
              style={{ border: '1px solid #E5E5E5' }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="bg-white rounded-3xl p-8 shadow-2xl overflow-hidden" style={{ border: '1px solid #E5E5E5' }}>
              <div className="cp-section-title" style={{ marginBottom: 6 }}>{t('campaigns.budgetModal.title', 'Presupuesto del evento')}</div>
              <p style={{ fontSize: 12.5, color: GRAY, marginBottom: 16 }}>{t('campaigns.budgetModal.desc', 'Se usa para calcular el costo por lead y por lead calificado.')}</p>

              <label className="cp-form-label">{t('campaigns.budgetModal.amount', 'Monto (CLP)')}</label>
              <input
                type="number"
                min="0"
                className="cp-form-input"
                placeholder={t('campaigns.form.budgetPlaceholder', 'Ej: 500000')}
                value={budgetInput}
                onChange={(e) => setBudgetInput(e.target.value)}
                style={{ marginBottom: 14 }}
                autoFocus
              />

              <div className="cp-modal-actions">
                <button className="cp-modal-btn" onClick={() => setShowBudgetModal(false)} disabled={savingBudget}>{t('hub.cancel', 'Cancelar')}</button>
                <button
                  className="cp-modal-btn cp-modal-btn--primary"
                  onClick={handleSaveBudget}
                  disabled={savingBudget}
                >
                  {savingBudget ? t('hub.saving', 'Guardando…') : t('hub.save', 'Guardar')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignsPanel;
